import { apiRequest } from "../api/client";

export const PASTE_LIST_STORAGE_KEY = "femtyz_paste_list_flow";
export const PASTE_LIST_FEATURES_OFFSET = 20;

const PLATFORM_ORDER = ["asda", "tesco", "morrison", "sainsbury"];

const PLATFORM_LABELS = {
  asda: "Asda",
  tesco: "Tesco",
  morrison: "Morrisons",
  sainsbury: "Sainsbury's",
};

export function toQueryListParam(input) {
  const raw = String(input || "").trim();
  if (!raw) return "";
  const parts = raw
    .split(/\r?\n|,|;|•|●|·|–|—|\s-\s|^\-\s+/gm)
    .map((x) => String(x || "").trim())
    .filter(Boolean);
  return Array.from(new Set(parts)).join(",");
}

function groupProviderRowsToProducts(rows) {
  const groupedBySku = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const normalized = normalizeProvider(row);
    const key = String(normalized.pid || normalized.sku || "").trim();
    if (!key || !normalized.title) return;
    if (!groupedBySku.has(key)) groupedBySku.set(key, []);
    groupedBySku.get(key).push(normalized);
  });

  return Array.from(groupedBySku.values())
    .map((group) => {
      const best = pickBestProvider(group);
      if (!best) return null;
      return {
        ...best,
        comparison: buildComparison(group),
      };
    })
    .filter(Boolean);
}

export function expandPasteLines(input) {
  const raw = String(input || "").trim();
  if (!raw) return [];
  const newlineParts = raw
    .split(/\r?\n/)
    .map((line) => String(line || "").trim())
    .filter(Boolean);
  if (newlineParts.length > 1) return newlineParts;
  const singleLine = newlineParts[0] || raw;
  const splitParts = toQueryListParam(singleLine)
    .split(",")
    .map((part) => String(part || "").trim())
    .filter(Boolean);
  if (splitParts.length > 1) return splitParts;
  return newlineParts.length ? newlineParts : splitParts;
}

export function isListPasteMode(lines) {
  return Array.isArray(lines) && lines.length > 1;
}

export async function fetchFeaturesProductsPage(
  dispatch,
  fetchActowizFeatures,
  { queryList, page, pageSize }
) {
  const targetStart = (page - 1) * pageSize;
  let collected = [];
  let apiStart = targetStart;
  let totalCount = 0;
  let basketTotals = null;
  let attempts = 0;
  const maxAttempts = 15;

  while (collected.length < pageSize && attempts < maxAttempts) {
    attempts += 1;
    const result = await dispatch(
      fetchActowizFeatures({
        query_list: queryList,
        start: apiStart,
        offset: pageSize,
        mode: "replace",
        force: true,
      })
    ).unwrap();

    if (typeof result?.totalCount === "number") {
      totalCount = result.totalCount;
    }
    if (!basketTotals?.cheapest && result?.basketTotals?.cheapest) {
      basketTotals = result.basketTotals;
    }

    const batch = Array.isArray(result?.items) ? result.items : [];
    const fetchedCount =
      typeof result?.fetchedCount === "number" ? result.fetchedCount : batch.length;

    if (!batch.length) break;

    const seen = new Set(collected.map((p) => String(p?.pid || p?.sku || "")));
    batch.forEach((item) => {
      const key = String(item?.pid || item?.sku || "");
      if (key && seen.has(key)) return;
      if (key) seen.add(key);
      collected.push(item);
    });

    apiStart += Math.max(fetchedCount, 1);
    if (totalCount && apiStart >= totalCount) break;
  }

  return {
    items: collected.slice(0, pageSize),
    totalCount: totalCount || collected.length,
    basketTotals,
  };
}

export async function fetchCatalogProductsPage(
  dispatch,
  fetchActowizProducts,
  { queryList, page, pageSize }
) {
  const providerBatchSize = pageSize * 4;
  let collected = [];
  let apiStart = (page - 1) * providerBatchSize;
  let totalCount = 0;
  let attempts = 0;
  const maxAttempts = 12;

  while (collected.length < pageSize && attempts < maxAttempts) {
    attempts += 1;
    const result = await dispatch(
      fetchActowizProducts({
        query_list: queryList,
        start: apiStart,
        offset: providerBatchSize,
        mode: "replace",
        force: true,
      })
    ).unwrap();

    if (typeof result?.totalCount === "number") {
      totalCount = result.totalCount;
    }

    const flatRows = Array.isArray(result?.items) ? result.items : [];
    if (!flatRows.length) break;

    const grouped = groupProviderRowsToProducts(flatRows);
    const seen = new Set(collected.map((p) => String(p?.pid || p?.sku || "")));
    grouped.forEach((item) => {
      const key = String(item?.pid || item?.sku || "");
      if (key && seen.has(key)) return;
      if (key) seen.add(key);
      collected.push(item);
    });

    apiStart += providerBatchSize;
    const providerTotal = typeof result?.totalCount === "number" ? result.totalCount : 0;
    if (providerTotal && apiStart >= providerTotal) break;
    if (flatRows.length < providerBatchSize) break;
  }

  return {
    items: collected.slice(0, pageSize),
    totalCount: totalCount || collected.length,
  };
}

export function parseListLines(input) {
  const raw = String(input || "").trim();
  if (!raw) return [];
  return raw
    .split(/\r?\n/)
    .map((line) => String(line || "").trim())
    .filter(Boolean);
}

export function loadPasteListFlow() {
  try {
    const raw = sessionStorage.getItem(PASTE_LIST_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function savePasteListFlow(data) {
  try {
    sessionStorage.setItem(PASTE_LIST_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }
}

export function clearPasteListFlow() {
  try {
    sessionStorage.removeItem(PASTE_LIST_STORAGE_KEY);
  } catch {
    // ignore
  }
}

function normalizePlatformKey(platform) {
  const p = String(platform || "").trim().toLowerCase();
  if (p === "morrisons") return "morrison";
  if (p === "sainsburry" || p === "sainsburys") return "sainsbury";
  return p;
}

function parsePrice(value) {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const normalized = String(value).trim().replace(",", ".").replace(/[^0-9.-]/g, "");
  if (!normalized) return null;
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeProvider(p) {
  const images = Array.isArray(p?.all_images)
    ? p.all_images
    : p?.main_image_url
    ? [p.main_image_url]
    : p?.image
    ? [p.image]
    : [];
  const response = p?.response && typeof p.response === "object" ? p.response : null;
  const matchTypeRaw =
    p?.match_type ?? p?.matchType ?? response?.match_type ?? response?.matchType ?? "";
  const productNameRaw =
    p?.product_name ?? p?.productName ?? response?.product_name ?? response?.productName ?? "";
  return {
    platform: normalizePlatformKey(p?.platform),
    pid: p?.product_id ?? p?.pid ?? p?.sku,
    sku: p?.sku || p?.product_id,
    title: p?.product_name || p?.title || p?.sku || "",
    product_name: String(productNameRaw || "").trim(),
    price: parsePrice(p?.price),
    match_type: String(matchTypeRaw || "").trim(),
    images,
    image: images[0] || "",
    url: p?.url || p?.product_url || p?.productUrl || null,
  };
}

function buildComparison(providers) {
  const byPlatform = {};
  (Array.isArray(providers) ? providers : []).forEach((provider) => {
    const normalized = normalizeProvider(provider);
    const platform = normalized.platform;
    if (!platform) return;
    const price = normalized.price;
    const prev = byPlatform[platform];
    if (Number.isFinite(price)) {
      if (!prev || !Number.isFinite(prev.price) || price < prev.price) {
        byPlatform[platform] = normalized;
      }
    } else if (!prev) {
      byPlatform[platform] = normalized;
    }
  });

  return PLATFORM_ORDER.map((platform) => ({
    platform,
    label: PLATFORM_LABELS[platform] || platform,
    price: Number.isFinite(byPlatform[platform]?.price) ? byPlatform[platform].price : null,
    product_name: byPlatform[platform]?.product_name || "",
    match_type: byPlatform[platform]?.match_type || "",
    url: byPlatform[platform]?.url || null,
  }));
}

function pickBestProvider(providers) {
  if (!Array.isArray(providers) || !providers.length) return null;
  let best = providers[0];
  let bestPrice = parsePrice(best?.price);
  for (const p of providers) {
    const pr = parsePrice(p?.price);
    if (Number.isFinite(pr) && (!Number.isFinite(bestPrice) || pr < bestPrice)) {
      best = p;
      bestPrice = pr;
    }
  }
  return normalizeProvider(best);
}

export function getMatchConfidence(matchType) {
  const value = String(matchType || "").trim().toLowerCase();
  if (value.includes("exact")) {
    return { label: "Exact", tone: "exact" };
  }
  if (value.includes("higher size")) {
    return { label: "Medium", tone: "medium" };
  }
  if (value.includes("high") || value.includes("close")) {
    return { label: "High", tone: "high" };
  }
  if (value.includes("medium") || value.includes("partial")) {
    return { label: "Medium", tone: "medium" };
  }
  if (value) {
    return { label: "High", tone: "high" };
  }
  return { label: "Medium", tone: "medium" };
}

export function buildMatchesFromLines(lines, products) {
  return lines.map((queryLine, index) => {
    const product = products[index] || null;
    const matchType =
      product?.match_type ||
      product?.comparison?.find((row) => row?.match_type)?.match_type ||
      "";
    const confidence = getMatchConfidence(matchType);
    return {
      id: `${index}-${queryLine}`,
      queryLine,
      product,
      matchedName: product?.product_name || product?.title || "",
      matchType,
      confidence,
      image: product?.image || product?.images?.[0] || "",
      comparison: product?.comparison || [],
    };
  });
}

function normalizeFeatureResults(payload) {
  const results = Array.isArray(payload?.results)
    ? payload.results
    : Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
    ? payload
    : [];

  return results
    .map((r) => {
      const providers = Array.isArray(r?.providers) ? r.providers : r && typeof r === "object" ? [r] : [];
      const normalizedProviders = providers.map((provider) =>
        normalizeProvider({
          ...provider,
          sku: provider?.sku ?? r?.sku ?? provider?.product_id,
        })
      );
      const best = pickBestProvider(normalizedProviders);
      if (!best?.title) return null;
      return {
        ...best,
        comparison: buildComparison(normalizedProviders),
      };
    })
    .filter(Boolean);
}

function normalizeProductsPayload(payload) {
  const data = payload;
  const catalogLike =
    Array.isArray(data) &&
    data.length === 1 &&
    data[0] &&
    typeof data[0] === "object" &&
    Array.isArray(data[0]?.products)
      ? data[0]
      : data && typeof data === "object" && Array.isArray(data?.products)
      ? data
      : null;

  const results = Array.isArray(catalogLike?.products)
    ? catalogLike.products
    : Array.isArray(data?.results)
    ? data.results
    : Array.isArray(data?.products)
    ? data.products
    : Array.isArray(data)
    ? data
    : [];

  const providers = (() => {
    if (Array.isArray(results) && results.length && Array.isArray(results[0]?.providers)) {
      return results.flatMap((group) => (Array.isArray(group?.providers) ? group.providers : []));
    }
    if (Array.isArray(results) && results.length && Array.isArray(results[0]?.results)) {
      return results.flatMap((group) => (Array.isArray(group?.results) ? group.results : []));
    }
    return results;
  })();

  const groupedBySku = new Map();
  providers.forEach((provider) => {
    const normalized = normalizeProvider(provider);
    const key = normalized.pid || normalized.sku || normalized.title;
    if (!key) return;
    if (!groupedBySku.has(key)) groupedBySku.set(key, []);
    groupedBySku.get(key).push(normalized);
  });

  return Array.from(groupedBySku.values())
    .map((group) => {
      const best = pickBestProvider(group);
      if (!best?.title) return null;
      return {
        ...best,
        comparison: buildComparison(group),
      };
    })
    .filter(Boolean);
}

export async function fetchPasteListMatches(lines) {
  const queryList = toQueryListParam(lines.join("\n"));
  if (!queryList) return [];

  const qs = new URLSearchParams();
  qs.set("query_list", queryList);
  qs.set("start", "0");
  qs.set("offset", String(PASTE_LIST_FEATURES_OFFSET));

  const featuresRes = await apiRequest(`api/actowiz/features?${qs.toString()}`, { method: "GET" });
  if (featuresRes.ok) {
    const products = normalizeFeatureResults(featuresRes.data);
    if (products.length) {
      return buildMatchesFromLines(lines, products);
    }
  }

  const productsQs = new URLSearchParams();
  productsQs.set("query_list", queryList);
  productsQs.set("start", "0");
  productsQs.set("offset", String(PASTE_LIST_FEATURES_OFFSET));
  const productsRes = await apiRequest(`api/actowiz/products?${productsQs.toString()}`, { method: "GET" });
  if (!productsRes.ok) {
    const errors = Array.isArray(productsRes.data?.errors) ? productsRes.data.errors : null;
    throw new Error(errors?.join("\n") || productsRes.data?.message || "Failed to match your list.");
  }

  const products = normalizeProductsPayload(productsRes.data);
  return buildMatchesFromLines(lines, products);
}

export function extractBasketPrices(payload) {
  if (!payload || typeof payload !== "object") return null;
  const prices = {
    asda: parsePrice(payload.asda_price),
    tesco: parsePrice(payload.tesco_price),
    morrison: parsePrice(payload.morrisons_price ?? payload.morrison_price),
    sainsbury: parsePrice(payload.sainsbury_price),
  };
  const hasAny = Object.values(prices).some((value) => Number.isFinite(value));
  return hasAny ? prices : null;
}

export function buildBasketTotalsFromApiPrices(source) {
  const prices =
    source &&
    (source.asda_price != null ||
      source.tesco_price != null ||
      source.morrisons_price != null ||
      source.morrison_price != null ||
      source.sainsbury_price != null)
      ? extractBasketPrices(source)
      : source;

  if (!prices) {
    return { rows: [], cheapest: null, savings: null, expensiveStore: null };
  }

  const rows = PLATFORM_ORDER.map((platform) => ({
    platform,
    label: PLATFORM_LABELS[platform] || platform,
    total: prices[platform],
    pricedCount: Number.isFinite(prices[platform]) ? 1 : 0,
  })).filter((row) => Number.isFinite(row.total) && row.total > 0);

  if (!rows.length) {
    return { rows: [], cheapest: null, savings: null, expensiveStore: null };
  }

  rows.sort((a, b) => a.total - b.total);
  const cheapest = rows[0];
  const expensive = rows[rows.length - 1];
  const savings = expensive.total - cheapest.total;

  return {
    rows: rows.map((row) => ({
      ...row,
      delta: row.total - cheapest.total,
      isBest: row.platform === cheapest.platform,
    })),
    cheapest,
    savings: savings > 0 ? savings : null,
    expensiveStore: expensive,
  };
}

export async function fetchPasteListBasketSummary(lines, { totalCount } = {}) {
  const expanded = Array.isArray(lines)
    ? lines.map((line) => String(line || "").trim()).filter(Boolean)
    : expandPasteLines(lines);
  const queryList = toQueryListParam(expanded.join("\n"));
  if (!queryList) return null;

  const qs = new URLSearchParams();
  qs.set("query_list", queryList);
  qs.set("start", "0");
  qs.set("offset", String(PASTE_LIST_FEATURES_OFFSET));

  const res = await apiRequest(`api/actowiz/features?${qs.toString()}`, { method: "GET" });
  if (!res.ok) return null;
  return buildBasketTotalsFromApiPrices(res.data);
}

export function calculateBasketTotals(matches) {
  const rows = PLATFORM_ORDER.map((platform) => ({
    platform,
    label: PLATFORM_LABELS[platform] || platform,
    total: 0,
    pricedCount: 0,
  }));

  (Array.isArray(matches) ? matches : []).forEach((match) => {
    const comparison = Array.isArray(match?.comparison)
      ? match.comparison
      : Array.isArray(match?.product?.comparison)
      ? match.product.comparison
      : [];
    comparison.forEach((row) => {
      const platform = normalizePlatformKey(row?.platform);
      const target = rows.find((r) => r.platform === platform);
      if (!target) return;
      const price = parsePrice(row?.price);
      if (Number.isFinite(price) && price > 0) {
        target.total += price;
        target.pricedCount += 1;
      }
    });
  });

  const pricedRows = rows.filter((r) => r.pricedCount > 0);
  if (!pricedRows.length) {
    return { rows: [], cheapest: null, savings: null, expensiveStore: null };
  }

  pricedRows.sort((a, b) => a.total - b.total);
  const cheapest = pricedRows[0];
  const expensive = pricedRows[pricedRows.length - 1];
  const savings = expensive.total - cheapest.total;

  return {
    rows: pricedRows.map((row) => ({
      ...row,
      delta: row.total - cheapest.total,
      isBest: row.platform === cheapest.platform,
    })),
    cheapest,
    savings: savings > 0 ? savings : null,
    expensiveStore: expensive,
  };
}
